import React, { useState, useRef, useEffect } from 'react';
import { Bell, Video, BookOpen, FileText, Layers, CheckCheck, X, RefreshCw, UserPlus, MessageCircle, QrCode, Award, RotateCcw } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { getCurrentUser } from '../../utils/auth';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); NotificationBell.scss deleted with it.
//
// Two keyframes left the sheet in opposite directions: `notif-badge-pop` moved to
// tailwind.config.js as `badge-pop` (any count badge wants it), and
// `notif-panel-in` was simply `drop-in`, which was already there.
//
// The panel's two-layer shadow AND its `:root[data-theme="dark"]` block are both
// gone: `shadow-popover` is the token for a surface that floats, and it is
// rgba(17,24,39,.18) in light and rgba(0,0,0,.55) in dark on its own. That dark
// block existed only because the light values were hardcoded.
//
// `#3b82f6` appeared four times — the "Mark all read" link, the unread row wash,
// its hover, and the unread dot. It is the brand now, so the one colour that says
// "this is new" follows the institute.
//
// ⚠️ The icon tile deliberately has NO default tone. `notif-item__icon--${type}`
// only matched five of the nine types the ICON_MAP knows, so `whatsapp_message`,
// `new_enquiry` and `video` have always rendered a colourless 32px tile. Kept
// exactly that way here — TONE[type] falls through to nothing — because giving
// them a colour is a design decision, not a conversion.
const PANEL =
  'tw-absolute tw-right-0 tw-top-[calc(100%+8px)] tw-z-dropdown tw-flex tw-max-h-[460px] ' +
  'tw-w-[380px] tw-animate-drop-in tw-flex-col tw-overflow-hidden tw-rounded ' +
  'tw-border tw-border-solid tw-border-line tw-bg-surface tw-shadow-popover ' +
  'max-[480px]:tw--right-2 max-[480px]:tw-w-[calc(100vw-16px)]';

const LIST =
  'tw-max-h-[380px] tw-flex-1 tw-overflow-y-auto ' +
  '[&::-webkit-scrollbar]:tw-w-1 ' +
  '[&::-webkit-scrollbar-thumb]:tw-rounded-[2px] [&::-webkit-scrollbar-thumb]:tw-bg-line';

const ITEM =
  'tw-relative tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-border-0 ' +
  'tw-border-b tw-border-solid tw-border-line-soft tw-px-4 tw-py-3 ' +
  'tw-transition-colors tw-duration-150 last:tw-border-b-0';

const ITEM_READ = `${ITEM} hover:tw-bg-surface-hover`;
const ITEM_UNREAD = `${ITEM} tw-bg-primary/[0.04] hover:tw-bg-primary/[0.08]`;

const ICON_TILE =
  'tw-mt-0.5 tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm';

const TONE = {
  class_started: 'tw-bg-success/10 tw-text-success',
  class_ended: 'tw-bg-danger/10 tw-text-danger',
  assignment_posted: 'tw-bg-info/10 tw-text-info',
  assignment_graded: 'tw-bg-success/10 tw-text-success',
  assignment_returned: 'tw-bg-warning/10 tw-text-warning',
  batch_update: 'tw-bg-chart-1/10 tw-text-chart-1',
  general: 'tw-bg-muted-fg/10 tw-text-muted-fg',
};

const JOIN_BTN =
  'tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[5px] tw-whitespace-nowrap ' +
  'tw-rounded-[6px] tw-border-none tw-bg-success tw-px-3 tw-py-[5px] tw-text-[0.7rem] ' +
  'tw-font-semibold tw-text-on-status tw-transition-[background,transform] tw-duration-150 ' +
  'hover:tw-bg-[color-mix(in_srgb,hsl(var(--success))_88%,black)] hover:tw--translate-y-px ' +
  'active:tw-translate-y-0';

const RELOAD_BTN =
  'tw-inline-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center ' +
  'tw-rounded-[6px] tw-border tw-border-solid tw-border-line tw-bg-surface ' +
  'tw-text-content-muted tw-transition-all tw-duration-150 ' +
  'hover:tw-border-primary hover:tw-bg-surface-hover hover:tw-text-primary';

const ICON_MAP = {
  video: Video,
  class_started: Video,
  class_ended: Video,
  assignment_posted: FileText,
  assignment_graded: Award,
  assignment_returned: RotateCcw,
  batch_update: Layers,
  new_enquiry: UserPlus,
  'user-plus': UserPlus,
  whatsapp_message: MessageCircle,
  qr_payment_submitted: QrCode,
  qr_payment_update: QrCode,
  general: Bell,
  default: Bell,
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
}

/**
 * `trigger` swaps the bell's button for a shell's own; it takes `icon`, `label`, `onClick` and
 * `unread`, the count, and draws its own mark for it.
 * `footer(close)` draws a line under the list, such as a link to a page of every notification.
 */
const NotificationBell = ({ trigger: Trigger, footer }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useSocket();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const user = getCurrentUser();
  const isStudent = user?.usertype === 'student';

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleNotifClick = (notif) => {
    if (!notif.read) {
      markAsRead(notif._id);
    }
    // `link` is what a stored row carries; `url` is what a live socket event
    // carries. Reading only `link` left every just-arrived notification —
    // WhatsApp, enquiry, call — doing nothing until a refresh reloaded it from
    // the API in the other shape.
    const target = notif.link || notif.url;
    // For class_started, students use the Join Class button instead
    if (target && !(isStudent && notif.type === 'class_started')) {
      window.location.href = target;
    }
    setOpen(false);
  };

  const handleJoinClass = (e, notif) => {
    e.stopPropagation();
    if (!notif.read) markAsRead(notif._id);
    const batchId = notif.batchId;
    if (batchId) {
      window.location.href = `/s/classroom/${batchId}`;
    }
    setOpen(false);
  };

  const handleReload = (e) => {
    e.stopPropagation();
    window.location.reload();
  };

  const badge = unreadCount > 0 && (
    <span className="tw-absolute tw-right-0.5 tw-top-0.5 tw-flex tw-h-4 tw-min-w-[16px] tw-animate-badge-pop tw-items-center tw-justify-center tw-rounded-sm tw-border-2 tw-border-solid tw-border-[var(--bg-primary)] tw-bg-danger tw-px-1 tw-text-[10px] tw-font-medium tw-leading-none tw-text-on-status">
      {unreadCount > 99 ? '99+' : unreadCount}
    </span>
  );

  return (
    <div className="tw-relative" ref={panelRef}>
      {Trigger ? (
        <Trigger icon={Bell} label="Notifications" unread={unreadCount} onClick={() => setOpen(prev => !prev)} />
      ) : (
        // nav-action-btn belongs to the topnav shell, not to this module — it stays.
        <button
          className="nav-action-btn tw-relative"
          onClick={() => setOpen(prev => !prev)}
          aria-label="Notifications"
        >
          <Bell size={20} />
          {badge}
        </button>
      )}

      {open && (
        <div className={PANEL}>
          {/* Header */}
          <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-4 tw-py-3.5">
            <h6 className="tw-m-0 tw-text-sm tw-font-medium tw-text-content">Notifications</h6>
            <div className="tw-flex tw-items-center tw-gap-2">
              {unreadCount > 0 && (
                <button
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-[6px] tw-border-none tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-primary tw-transition-colors tw-duration-150 hover:tw-bg-primary/[0.08]"
                  onClick={markAllAsRead}
                  title="Mark all as read"
                >
                  <CheckCheck size={15} />
                  Mark all read
                </button>
              )}
              <button
                className="tw-flex tw-cursor-pointer tw-items-center tw-rounded-[6px] tw-border-none tw-bg-transparent tw-p-1 tw-text-content-subtle hover:tw-bg-surface-hover hover:tw-text-content"
                onClick={() => setOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className={LIST}>
            {notifications.length === 0 ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-12 tw-text-content-subtle">
                <Bell size={32} strokeWidth={1.5} />
                <p className="tw-m-0 tw-text-[0.8125rem]">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const IconComp = ICON_MAP[notif.type] || ICON_MAP[notif.icon] || ICON_MAP.default;
                return (
                  <div
                    key={notif._id}
                    className={notif.read ? ITEM_READ : ITEM_UNREAD}
                    onClick={() => handleNotifClick(notif)}
                  >
                    <div className={`${ICON_TILE} ${TONE[notif.type] || ''}`}>
                      <IconComp size={16} />
                    </div>
                    <div className="tw-min-w-0 tw-flex-1">
                      <p className="tw-mb-0.5 tw-mt-0 tw-text-[0.8125rem] tw-font-semibold tw-leading-[1.3] tw-text-content">
                        {notif.title}
                      </p>
                      {notif.type === 'whatsapp_message' && (notif.phone || notif.meta?.phone) && (
                        <p className="tw-mb-0.5 tw-mt-0 tw-text-[0.72rem] tw-font-medium tw-leading-[1.3] tw-text-content-muted">
                          {notif.phone || notif.meta?.phone}
                        </p>
                      )}
                      <p className="tw-mb-1 tw-mt-0 tw-line-clamp-2 tw-text-xs tw-leading-[1.4] tw-text-content-muted">
                        {notif.message || notif.body}
                      </p>
                      {/* Action buttons for class_started notifications (students) */}
                      {isStudent && notif.type === 'class_started' && notif.batchId && (
                        <div className="tw-mb-0.5 tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5">
                          <button
                            className={JOIN_BTN}
                            onClick={(e) => handleJoinClass(e, notif)}
                          >
                            <Video size={13} />
                            Join Class
                          </button>
                          <button
                            className={RELOAD_BTN}
                            onClick={handleReload}
                            title="Reload page"
                          >
                            <RefreshCw size={13} />
                          </button>
                        </div>
                      )}
                      <span className="tw-text-[0.6875rem] tw-text-content-subtle">{timeAgo(notif.createdAt)}</span>
                    </div>
                    {!notif.read && (
                      <span className="tw-mt-1.5 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-primary" />
                    )}
                  </div>
                );
              })
            )}
          </div>
          {footer && (
            <div className="tw-flex tw-shrink-0 tw-justify-center tw-border-0 tw-border-t tw-border-solid tw-border-line tw-px-4 tw-py-3">
              {footer(() => setOpen(false))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
